import { formatDistanceToNow, format } from 'date-fns';
import { nb, enUS } from 'date-fns/locale';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useLanguage } from '@/hooks/use-language';
import { useAuth } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';

interface CommentItemProps {
  comment: {
    id: string;
    content: string;
    created_at: string;
    is_edited: boolean;
    user_id: string;
    user_first_name?: string;
    user_last_name?: string;
    parent_comment_id?: string | null;
    parent_user_first_name?: string;
    parent_user_last_name?: string;
  };
  onDelete?: () => void;
  onReply?: (commentId: string, userName: string) => void;
}

export function CommentItem({ comment, onDelete, onReply }: CommentItemProps) {
  const { language, t } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);

  const locale = language === 'no' ? nb : enUS;
  const userName = `${comment.user_first_name || 'Unknown'} ${comment.user_last_name || 'User'}`;
  const initials = `${comment.user_first_name?.[0] || ''}${comment.user_last_name?.[0] || ''}`.toUpperCase();

  const handleDelete = async () => {
    if (!window.confirm(t('event.comments.delete_confirm'))) return;

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('event_comments')
        .delete()
        .eq('id', comment.id);

      if (error) throw error;

      toast({
        title: t('event.comments.deleted') || 'Comment deleted',
        duration: 2000,
      });

      onDelete?.();
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast({
        title: t('event.comments.delete_error') || 'Failed to delete comment',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Highlight mentions in content (limit to 3 words max)
  const renderContent = (text: string) => {
    const mentionRegex = /@([A-Za-zæøåÆØÅ]+(?:\s+[A-Za-zæøåÆØÅ]+){0,2})(?=\s|[.,!?]|$)/g;
    const parts = text.split(mentionRegex);
    
    return parts.map((part, index) => {
      if (index % 2 === 1) {
        // This is a mention
        return (
          <span key={index} className="text-primary font-semibold">
            @{part}
          </span>
        );
      }
      return part;
    });
  };

  return (
    <div className="flex gap-3 py-3">
      <Avatar className="w-10 h-10 flex-shrink-0">
        <AvatarFallback className="bg-primary text-primary-foreground">
          {initials}
        </AvatarFallback>
      </Avatar>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm">{userName}</span>
          <span 
            className="text-xs text-muted-foreground cursor-help" 
            title={format(new Date(comment.created_at), 'HH:mm:ss')}
          >
            {formatDistanceToNow(new Date(comment.created_at), { 
              addSuffix: true, 
              locale 
            })}
          </span>
          {comment.is_edited && (
            <span className="text-xs text-muted-foreground italic">
              {t('event.comments.edited')}
            </span>
          )}
        </div>
        
        {comment.parent_comment_id && (comment.parent_user_first_name || comment.parent_user_last_name) && (
          <div className="text-xs text-muted-foreground mb-1">
            {t('event.comments.replying_to', { 
              name: `${comment.parent_user_first_name || ''} ${comment.parent_user_last_name || ''}`.trim() 
            })}
          </div>
        )}
        
        <p className="text-sm mt-1 whitespace-pre-wrap break-words">
          {renderContent(comment.content)}
        </p>

        {(user?.id === comment.user_id || onReply) && (
          <div className="mt-2 flex gap-2">
            {onReply && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onReply(comment.id, userName)}
                className="h-7 px-2 text-xs"
              >
                {t('event.comments.reply')}
              </Button>
            )}
            {user?.id === comment.user_id && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                disabled={isDeleting}
                className="h-7 px-2 text-xs text-destructive hover:text-destructive"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                {t('event.comments.delete')}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
